/**
 * TRUS-709: the member Inbox — one row per member conversation (the member's
 * DM), from GET /api/messaging/inbox/member-conversations.
 *
 *  - View chips: All · Mine · Unassigned · Needs reply (· Unrouted for admins
 *    when the org manages member conversations by caseload);
 *  - channel and program filters, member-name search, infinite scroll;
 *  - each row: member, preview, time, unread, owner avatars and
 *    "Last reply: <name> · <time>" (or "Needs reply");
 *  - TRUS-745: a Pinned section on top (`?pinned=true`, the caller's pins in pin
 *    order, whatever page they would fall on) and a pin toggle on every row.
 *
 * An older backend without the endpoint (404) falls back to the member DMs in
 * the channel list, filtered here as far as their fields allow.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, MailOpen, Pin, PinOff, Plus, Search, UserCircle2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { messagingApi, type Channel } from '@/services/messagingApi';
import {
  conversationFromChannel,
  memberInboxApi,
  type InboxChannelFilter,
  type InboxView,
  type MemberConversation,
} from '@/services/memberInboxApi';
import { programsAPI } from '@/services/api/programs';
import { getCurrentUserId } from '@/utils/auth';
import { MessageChannelBadge } from '../MessageChannelBadge';
import { toast } from '../../ui/use-toast';
import { errorCode } from '../errorDetail';
import { MAX_PINS } from '../channelPins';
import { MEMBER_INBOX_KEY, initialsOf, shortTime } from './memberInbox';
import { SwipeRow } from '../swipe/SwipeRow';
import { swipeSides, useSwipeSettings } from '../swipe/swipeSettings';
import { useConversationReadToggle } from '../swipe/useConversationReadToggle';

export { MEMBER_INBOX_KEY };
const PAGE_SIZE = 50;

interface MemberInboxListProps {
  selectedChannelId?: number | null;
  onSelect: (conversation: MemberConversation) => void;
  /** Org admin / platform admin: may see the Unrouted lane. */
  isAdmin: boolean;
  /** The org manages member conversations by caseload (settings). */
  caseloadMode: boolean;
  onCreateDM?: () => void;
  /** Told whether the new endpoint answered (false = channel-list fallback). */
  onSourceChange?: (source: 'inbox' | 'fallback') => void;
}

/** A Channel for MessageThread built from an Inbox row (the detail query fills the rest). */
export function channelFromConversation(row: MemberConversation): Channel {
  return {
    id: row.channel_id,
    uuid: '',
    name: row.member_name,
    display_name: row.member_name,
    description: null,
    topic: null,
    channel_type: 'direct',
    is_dm: true,
    is_archived: false,
    is_readonly: false,
    member_count: 0,
    message_count: 0,
    last_message_at: row.last_activity_at,
    last_message_preview: row.preview,
    icon: null,
    color: null,
    unread_count: row.unread_count,
    mention_count: 0,
    created_at: '',
    created_by_name: null,
    dm_member_id: row.member_id,
    // The header reads these straight away, before the detail arrives.
    owners: row.owners,
    responders: row.responders,
    last_staff_reply: row.last_staff_reply,
    needs_reply: row.needs_reply,
  } as Channel;
}

/** Client-side filters for the fallback list (the server does them otherwise). */
export function filterFallback(
  rows: MemberConversation[],
  view: InboxView,
  q: string,
  myUserId: number | null,
): MemberConversation[] {
  const needle = q.trim().toLowerCase();
  return rows.filter((r) => {
    if (needle && !r.member_name.toLowerCase().includes(needle)) return false;
    if (view === 'unassigned') return r.owners.length === 0;
    if (view === 'needs_reply') return r.needs_reply;
    if (view === 'mine') {
      return (
        myUserId != null &&
        (r.owners.some((o) => o.user_id === myUserId) || r.responders.some((o) => o.user_id === myUserId))
      );
    }
    return true;
  });
}

export function MemberInboxList({
  selectedChannelId,
  onSelect,
  isAdmin,
  caseloadMode,
  onCreateDM,
  onSourceChange,
}: MemberInboxListProps) {
  const { t } = useAppTranslation();
  const myUserId = getCurrentUserId();
  const [view, setView] = useState<InboxView>('all');
  const [channelFilter, setChannelFilter] = useState<InboxChannelFilter | ''>('');
  const [programId, setProgramId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;

  // Debounce the search box into the query.
  useEffect(() => {
    const h = setTimeout(() => setQ(search), 300);
    return () => clearTimeout(h);
  }, [search]);

  const showUnrouted = isAdmin && caseloadMode;
  useEffect(() => {
    if (!showUnrouted && view === 'unrouted') setView('all');
  }, [showUnrouted, view]);

  const list = useInfiniteQuery({
    queryKey: [MEMBER_INBOX_KEY, cboId, view, channelFilter, programId, q],
    queryFn: ({ pageParam }) =>
      memberInboxApi.listMemberConversations({
        view,
        channel: channelFilter || null,
        program_id: programId,
        q,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last?.next_cursor ?? undefined,
    refetchInterval: 30_000,
    retry: false,
  });

  const missingEndpoint = list.isSuccess && list.data?.pages[0] === null;

  // TRUS-745: the caller's pins, same filters. Only rows the server flags as
  // pinned count, so a backend that ignores `pinned` can't pin everything.
  const pinnedQuery = useQuery({
    queryKey: [MEMBER_INBOX_KEY, cboId, 'pinned', view, channelFilter, programId, q],
    queryFn: () =>
      memberInboxApi.listMemberConversations({
        view,
        channel: channelFilter || null,
        program_id: programId,
        q,
        pinned: true,
        limit: 100,
      }),
    enabled: list.isSuccess && !missingEndpoint,
    refetchInterval: 30_000,
    retry: false,
  });
  const pinnedRows: MemberConversation[] = useMemo(
    () =>
      (pinnedQuery.data?.items || [])
        .filter((r) => r.is_pinned === true)
        .sort((a, b) => (a.pin_position ?? Number.MAX_SAFE_INTEGER) - (b.pin_position ?? Number.MAX_SAFE_INTEGER)),
    [pinnedQuery.data],
  );

  const qc = useQueryClient();
  const togglePin = useMutation({
    mutationFn: async (row: MemberConversation) => {
      if (row.is_pinned) await messagingApi.unpinChannel(row.channel_id);
      else await messagingApi.pinChannel(row.channel_id);
    },
    onError: (error: Error, row) => {
      toast({
        title:
          errorCode(error) === 'pin_limit'
            ? t('messaging.pins.limit', 'You can pin up to {{max}} conversations', { max: MAX_PINS })
            : row.is_pinned
              ? t('messaging.pins.unpinFailed', 'Could not unpin conversation')
              : t('messaging.pins.pinFailed', 'Could not pin conversation'),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [MEMBER_INBOX_KEY] });
      void qc.invalidateQueries({ queryKey: ['messaging-channels'] });
    },
  });

  // Older backend: member DMs from the channel list.
  const fallback = useQuery({
    queryKey: ['messaging-channels', 'member-inbox-fallback'],
    queryFn: () => messagingApi.listChannels(),
    enabled: missingEndpoint,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (list.isSuccess) onSourceChange?.(missingEndpoint ? 'fallback' : 'inbox');
  }, [list.isSuccess, missingEndpoint, onSourceChange]);

  const { data: programs = [] } = useQuery({
    queryKey: ['member-inbox-programs', cboId],
    queryFn: () => programsAPI.listPrograms({ is_active: true }),
    enabled: !missingEndpoint,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const rows: MemberConversation[] = useMemo(() => {
    if (missingEndpoint) {
      const dms = (fallback.data || []).filter((c) => c.dm_member_id != null).map(conversationFromChannel);
      return filterFallback(dms, view, q, myUserId);
    }
    // Pinned rows show in their own section, not twice.
    const seen = new Set<number>(pinnedRows.map((r) => r.channel_id));
    const out: MemberConversation[] = [];
    for (const page of list.data?.pages || []) {
      for (const row of page?.items || []) {
        if (!seen.has(row.channel_id)) {
          seen.add(row.channel_id);
          out.push(row);
        }
      }
    }
    return out;
  }, [missingEndpoint, fallback.data, list.data, view, q, myUserId, pinnedRows]);

  // Infinite scroll: load the next page when the sentinel comes into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const chips: Array<{ id: InboxView; label: string }> = [
    { id: 'all', label: t('messaging.inbox.viewAll', 'All') },
    { id: 'mine', label: t('messaging.inbox.viewMine', 'Mine') },
    { id: 'unassigned', label: t('messaging.inbox.viewUnassigned', 'Unassigned') },
    { id: 'needs_reply', label: t('messaging.inbox.viewNeedsReply', 'Needs reply') },
    ...(showUnrouted ? [{ id: 'unrouted' as InboxView, label: t('messaging.inbox.viewUnrouted', 'Unrouted') }] : []),
  ];

  const loading = list.isLoading || (missingEndpoint && fallback.isLoading);
  const unreadTotal = [...pinnedRows, ...rows].reduce((sum, r) => sum + (r.unread_count || 0), 0);
  const canPin = !missingEndpoint;
  const readToggle = useConversationReadToggle();
  const renderRow = (row: MemberConversation) => (
    <MemberInboxRow
      key={row.channel_id}
      row={row}
      selected={selectedChannelId === row.channel_id}
      onClick={() => onSelect(row)}
      onTogglePin={canPin ? () => togglePin.mutate(row) : undefined}
      onToggleRead={() => readToggle.mutate({ channelId: row.channel_id, unread: row.unread_count > 0 })}
    />
  );

  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-slate-900" data-testid="member-inbox-list">
      <div className="space-y-2 border-b p-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t('messaging.inbox.title', 'Member conversations')}</h2>
          <div className="flex items-center gap-1">
            {unreadTotal > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                {unreadTotal > 99 ? '99+' : unreadTotal}
              </span>
            )}
            {onCreateDM && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={onCreateDM}
                title={t('messaging.inbox.newConversation', 'New conversation')}
                aria-label={t('messaging.inbox.newConversation', 'New conversation')}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('messaging.inbox.searchPlaceholder', 'Search members…')}
            aria-label={t('messaging.inbox.searchPlaceholder', 'Search members…')}
            className="h-8 pl-8 text-sm"
            data-testid="member-inbox-search"
          />
        </div>
        <div className="flex flex-wrap gap-1" role="tablist" aria-label={t('messaging.inbox.viewsLabel', 'Views')}>
          {chips.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={view === c.id}
              data-testid={`member-inbox-view-${c.id}`}
              onClick={() => setView(c.id)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                view === c.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-slate-300 bg-white text-muted-foreground hover:text-foreground dark:border-slate-700 dark:bg-slate-800',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        {!missingEndpoint && (
          <div className="flex gap-2">
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value as InboxChannelFilter | '')}
              aria-label={t('messaging.inbox.channelFilter', 'Channel')}
              data-testid="member-inbox-channel"
              className="h-7 min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-1.5 text-xs dark:border-slate-700 dark:bg-slate-800"
            >
              <option value="">{t('messaging.inbox.anyChannel', 'Any channel')}</option>
              <option value="portal">{t('messaging.replyChannel.portal', 'Portal')}</option>
              <option value="sms">{t('messaging.replyChannel.sms', 'SMS')}</option>
              <option value="email">{t('messaging.replyChannel.email', 'Email')}</option>
              <option value="call">{t('messaging.inbox.channelCall', 'Call')}</option>
            </select>
            {programs.length > 0 && (
              <select
                value={programId ?? ''}
                onChange={(e) => setProgramId(e.target.value ? Number(e.target.value) : null)}
                aria-label={t('messaging.inbox.programFilter', 'Program')}
                data-testid="member-inbox-program"
                className="h-7 min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-1.5 text-xs dark:border-slate-700 dark:bg-slate-800"
              >
                <option value="">{t('messaging.inbox.anyProgram', 'Any program')}</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto" role="list" aria-label={t('messaging.inbox.title', 'Member conversations')}>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : list.isError ? (
          <div className="px-4 py-6 text-sm text-destructive">
            {t('messaging.inbox.loadFailed', 'Could not load member conversations.')}
          </div>
        ) : rows.length === 0 && pinnedRows.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground" data-testid="member-inbox-empty">
            {view === 'all' && !q
              ? t('messaging.inbox.empty', 'No member conversations yet')
              : t('messaging.inbox.emptyFiltered', 'No conversations match these filters')}
          </div>
        ) : (
          <>
            {pinnedRows.length > 0 && (
              <div data-testid="member-inbox-pinned" className="border-b-2 border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-1 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Pin className="h-3 w-3" />
                  {t('messaging.pins.section', 'Pinned')}
                </div>
                {pinnedRows.map(renderRow)}
              </div>
            )}
            {rows.map(renderRow)}
          </>
        )}
        {!missingEndpoint && hasNextPage && (
          <div ref={sentinelRef} className="flex justify-center py-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
              data-testid="member-inbox-load-more"
            >
              {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : t('messaging.inbox.loadMore', 'Load more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function MemberInboxRow({
  row,
  selected,
  onClick,
  onTogglePin,
  onToggleRead,
}: {
  row: MemberConversation;
  selected: boolean;
  onClick: () => void;
  /** TRUS-745: pin / unpin for the caller (absent on the fallback list). */
  onTogglePin?: () => void;
  /** TRUS-759: mark read when unread, else mark unread. */
  onToggleRead?: () => void;
}) {
  const { t } = useAppTranslation();
  const swipeSettings = useSwipeSettings();
  const unread = row.unread_count > 0;
  const owners = row.owners.slice(0, 3);
  const extraOwners = row.owners.length - owners.length;
  const pinLabel = row.is_pinned ? t('messaging.pins.unpin', 'Unpin') : t('messaging.pins.pin', 'Pin');
  const readLabel = unread
    ? t('messaging.readState.markRead', 'Mark read')
    : t('messaging.readState.markUnread', 'Mark unread');
  const name = row.member_name || t('messaging.inbox.unnamedMember', 'Member');
  // TRUS-761: which side pins and which marks read / unread is the user's setting.
  const sides = swipeSides(swipeSettings.conversations, {
    pin: onTogglePin ? { label: pinLabel, icon: row.is_pinned ? PinOff : Pin, tone: 'pin' as const, onCommit: onTogglePin } : undefined,
    read: onToggleRead ? { label: readLabel, icon: unread ? MailOpen : Mail, tone: 'read' as const, onCommit: onToggleRead } : undefined,
  });
  return (
    <SwipeRow
      right={sides.right}
      left={sides.left}
      contentClassName="bg-slate-50 dark:bg-slate-900"
      outerProps={{ role: 'listitem', className: 'group', 'data-testid': `member-inbox-swipe-${row.channel_id}` }}
    >
    <button
      type="button"
      onClick={onClick}
      data-testid={`member-inbox-row-${row.channel_id}`}
      aria-current={selected || undefined}
      className={cn(
        'flex w-full gap-2.5 border-b px-3 py-2.5 text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800',
        selected && 'bg-slate-200 dark:bg-slate-700',
      )}
    >
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
        {row.member_name ? initialsOf(row.member_name) : <UserCircle2 className="h-4 w-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className={cn('truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>
            {row.member_name || t('messaging.inbox.unnamedMember', 'Member')}
          </span>
          <span className="flex-shrink-0 text-[11px] text-muted-foreground">{shortTime(row.last_activity_at, t)}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className={cn('min-w-0 flex-1 truncate text-xs', unread ? 'text-foreground' : 'text-muted-foreground')}>
            {row.preview || '—'}
          </span>
          {unread && (
            <span
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground"
              data-testid="member-inbox-unread"
            >
              {row.unread_count > 99 ? '99+' : row.unread_count}
            </span>
          )}
        </span>
        <span className="mt-1 flex items-center gap-1.5">
          {row.last_channel && <MessageChannelBadge channel={row.last_channel} />}
          {row.needs_reply ? (
            <span
              className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
              data-testid="member-inbox-needs-reply"
            >
              {t('messaging.inbox.needsReply', 'Needs reply')}
            </span>
          ) : row.last_staff_reply ? (
            <span className="min-w-0 truncate text-[11px] text-muted-foreground" data-testid="member-inbox-last-reply">
              {t('messaging.inbox.lastReply', 'Last reply: {{name}} · {{time}}', {
                name: row.last_staff_reply.name || t('messaging.sender.staff', 'Staff'),
                time: shortTime(row.last_staff_reply.at, t),
              })}
            </span>
          ) : null}
          {owners.length > 0 && (
            <span className="ml-auto flex flex-shrink-0 -space-x-1.5" aria-label={t('messaging.owners.label', 'Owners')}>
              {owners.map((o) => (
                <span
                  key={o.user_id}
                  title={o.name}
                  data-testid="member-inbox-owner"
                  className="flex h-5 w-5 items-center justify-center rounded-full border border-white bg-indigo-100 text-[9px] font-semibold text-indigo-700 dark:border-slate-900 dark:bg-indigo-900/50 dark:text-indigo-200"
                >
                  {initialsOf(o.name)}
                </span>
              ))}
              {extraOwners > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-white bg-slate-200 text-[9px] font-semibold text-slate-600 dark:border-slate-900 dark:bg-slate-700 dark:text-slate-200">
                  {t('messaging.inbox.moreCount', '+{{count}}', { count: extraOwners })}
                </span>
              )}
            </span>
          )}
        </span>
      </span>
    </button>
    {onTogglePin && (
      <button
        type="button"
        onClick={onTogglePin}
        title={pinLabel}
        aria-label={`${pinLabel}: ${name}`}
        aria-pressed={!!row.is_pinned}
        data-testid={`member-inbox-pin-${row.channel_id}`}
        // Over the row's time, on hover / keyboard focus; always on touch screens
        // (no hover there), like the Team row menu.
        className="absolute right-2 top-1.5 rounded bg-slate-100 p-1 text-muted-foreground opacity-0 hover:text-foreground focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100 dark:bg-slate-800"
      >
        {row.is_pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
      </button>
    )}
    {onToggleRead && (
      <button
        type="button"
        onClick={onToggleRead}
        title={readLabel}
        aria-label={`${readLabel}: ${name}`}
        data-testid={`member-inbox-read-${row.channel_id}`}
        // Mouse / keyboard twin of the left swipe; touch screens swipe instead.
        className={cn(
          'absolute top-1.5 rounded bg-slate-100 p-1 text-muted-foreground opacity-0 hover:text-foreground focus:opacity-100 group-hover:opacity-100 dark:bg-slate-800',
          onTogglePin ? 'right-9' : 'right-2',
        )}
      >
        {unread ? <MailOpen className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
      </button>
    )}
    </SwipeRow>
  );
}

export default MemberInboxList;
